import { SpecialHours, SpecialHoursRange } from '../../../core/business/business';

// Pure helpers for holiday & special hours (ENG-2393). Dates are 'YYYY-MM-DD'
// strings in the business timezone; they are handled as UTC calendar days so
// the browser timezone never shifts a date.

export const SPECIAL_HOURS_REASON_MAX_LENGTH = 200;
export const SPECIAL_HOURS_MAX_RANGES = 5;

const DEFAULT_RANGE: SpecialHoursRange = { open: '09:00', close: '17:00' };
const LAST_SLOT_MINUTES = 23 * 60 + 45;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface TimeOption {
  value: string;
  label: string;
}

export function todayInTimezone(timezone?: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  } catch {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  }
}

export function parseDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

export function toDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  const parsed = parseDate(date);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return toDateString(parsed);
}

export function weekdayName(date: string): string {
  return WEEKDAYS[parseDate(date).getUTCDay()];
}

export function sortDates(dates: string[]): string[] {
  return Array.from(new Set(dates)).sort();
}

export function lastDate(entry: SpecialHours): string {
  return sortDates(entry.dates || []).slice(-1)[0];
}

// An entry expires once its last date is before today (business timezone).
export function isExpired(entry: SpecialHours, today: string): boolean {
  const last = lastDate(entry);
  return !last || last < today;
}

export function activeSpecialHours(entries: SpecialHours[] | null | undefined, today: string): SpecialHours[] {
  return (entries || [])
    .filter(entry => !isExpired(entry, today))
    .map(entry => normalizeSpecialHours(entry))
    .sort((a, b) => a.dates[0].localeCompare(b.dates[0]));
}

export function normalizeSpecialHours(entry: SpecialHours): SpecialHours {
  return {
    id: entry.id,
    dates: sortDates(entry.dates || []),
    closed: !!entry.closed,
    hours: entry.closed ? [] : (entry.hours || []).map(range => ({ open: range.open, close: range.close })),
    reason: entry.reason?.trim() ? entry.reason.trim() : null,
  };
}

// Groups dates into runs of consecutive days.
export function dateRuns(dates: string[]): string[][] {
  const runs: string[][] = [];
  sortDates(dates).forEach(date => {
    const run = runs[runs.length - 1];
    if (run && addDays(run[run.length - 1], 1) === date) {
      run.push(date);
    } else {
      runs.push([date]);
    }
  });
  return runs;
}

// 'Wed, Nov 11' | 'Dec 20 – 29' | 'Dec 30 – Jan 2' | 'Oct 22 – 23, Oct 27'.
// The year shows only for dates outside the current year.
export function formatDatesLabel(dates: string[], today: string): string {
  const currentYear = parseDate(today).getUTCFullYear();
  const runs = dateRuns(dates);
  if (runs.length === 1 && runs[0].length === 1) {
    const date = parseDate(runs[0][0]);
    const weekday = WEEKDAYS[date.getUTCDay()].slice(0, 3);
    return `${weekday}, ${formatMonthDay(date)}${yearSuffix(date, currentYear)}`;
  }
  return runs.map(run => formatRun(run, currentYear)).join(', ');
}

function formatRun(run: string[], currentYear: number): string {
  const start = parseDate(run[0]);
  if (run.length === 1) {
    return `${formatMonthDay(start)}${yearSuffix(start, currentYear)}`;
  }
  const end = parseDate(run[run.length - 1]);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
  if (sameMonth) {
    return `${formatMonthDay(start)} – ${end.getUTCDate()}${yearSuffix(end, currentYear)}`;
  }
  if (!sameYear) {
    return `${formatMonthDay(start)}, ${start.getUTCFullYear()} – ${formatMonthDay(end)}, ${end.getUTCFullYear()}`;
  }
  return `${formatMonthDay(start)} – ${formatMonthDay(end)}${yearSuffix(end, currentYear)}`;
}

function formatMonthDay(date: Date): string {
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

function yearSuffix(date: Date, currentYear: number): string {
  return date.getUTCFullYear() === currentYear ? '' : `, ${date.getUTCFullYear()}`;
}

export function toMinutes(time: string): number {
  const [hour, minute] = (time || '').split(':').map(Number);
  return (hour || 0) * 60 + (minute || 0);
}

export function fromMinutes(minutes: number): string {
  const clamped = Math.max(0, Math.min(LAST_SLOT_MINUTES, minutes));
  const hour = Math.floor(clamped / 60);
  const minute = clamped % 60;
  return `${hour < 10 ? '0' : ''}${hour}:${minute < 10 ? '0' : ''}${minute}`;
}

// '14:00' → '2:00 pm'.
export function formatTime(time: string): string {
  const minutes = toMinutes(time);
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${minute < 10 ? '0' : ''}${minute} ${hour < 12 ? 'am' : 'pm'}`;
}

export function formatHoursLabel(entry: SpecialHours): string {
  if (entry.closed || !entry.hours?.length) {
    return 'Closed';
  }
  return entry.hours.map(range => `${formatTime(range.open)} – ${formatTime(range.close)}`).join(', ');
}

export function timeOptions(): TimeOption[] {
  const options: TimeOption[] = [];
  for (let minutes = 0; minutes <= LAST_SLOT_MINUTES; minutes += 15) {
    const value = fromMinutes(minutes);
    options.push({ value, label: formatTime(value) });
  }
  return options;
}

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// First range for custom hours: the regular hours of the first selected
// date's weekday, or 9:00 am – 5:00 pm when that day is closed.
export function defaultRange(dates: string[], regularHours: { day: string; open: string; close: string }[]): SpecialHoursRange {
  const first = sortDates(dates)[0];
  if (!first) {
    return { ...DEFAULT_RANGE };
  }
  const day = weekdayName(first).toLowerCase();
  const regular = (regularHours || []).find(hour => hour.day?.toLowerCase() === day);
  if (regular && TIME_PATTERN.test(regular.open) && TIME_PATTERN.test(regular.close) && toMinutes(regular.open) < toMinutes(regular.close)) {
    return { open: regular.open, close: regular.close };
  }
  return { ...DEFAULT_RANGE };
}

// An extra range starts one hour after the previous close and lasts two hours.
export function nextRange(previous?: SpecialHoursRange): SpecialHoursRange {
  if (!previous) {
    return { ...DEFAULT_RANGE };
  }
  const open = toMinutes(previous.close) + 60;
  return { open: fromMinutes(open), close: fromMinutes(open + 120) };
}

export function areRangesValid(ranges: SpecialHoursRange[]): boolean {
  if (!ranges?.length || ranges.length > SPECIAL_HOURS_MAX_RANGES) {
    return false;
  }
  const sorted = [...ranges].sort((a, b) => toMinutes(a.open) - toMinutes(b.open));
  return sorted.every((range, index) => {
    const isOrdered = toMinutes(range.open) < toMinutes(range.close);
    const previous = sorted[index - 1];
    return isOrdered && (!previous || toMinutes(previous.close) <= toMinutes(range.open));
  });
}

export function createSpecialHoursId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.random() * 16 | 0;
    return (char === 'x' ? random : (random & 0x3 | 0x8)).toString(16);
  });
}
